import JobCard, {
  elapsed,
  humanDuration,
  type QueueJob,
} from "@app/components/account-queue/job-card";
import { environment } from "@app/relay/relay-environment";
import { Link } from "@tanstack/react-router";
import { graphql } from "react-relay";
import { commitMutation, useLazyLoadQuery } from "react-relay";
import { useState } from "react";

import { cancelMutation, enqueueMutation } from "@app/components/account-queue/queue-fragment";
import type { jobDetailPage_Query } from "@app/queries/jobDetailPage_Query.graphql";
import type { queueFragment_CancelMutation } from "@app/queries/queueFragment_CancelMutation.graphql";
import type { queueFragment_EnqueueMutation } from "@app/queries/queueFragment_EnqueueMutation.graphql";

// One job, at a stable address.
//
// The queue page can only show a job among the others around it. This is what a
// person pastes into a support thread, and what "why did this fail?" resolves
// to three weeks later — the row is never deleted, so the link never rots.
export const jobDetailQuery = graphql`
  query jobDetailPage_Query($jobId: Int!) {
    accountJob(jobId: $jobId) {
      jobId
      kind
      label
      status
      phase
      currentObject
      rowsProcessed
      rowsTotal
      unit
      errorMessage
      params
      source
      requestedBy
      parentJobId
      supersededByJobId
      attempts
      startedAt
      finishedAt
      createdAt
      cancelRequestedAt
      stalled
      terminal
      queuePosition
      blocker
      stagesTotal
      stagesDone
      stageLabels {
        key
        label
        state
      }
      outcome {
        key
        value
      }
      typicalSeconds
      etaSeconds
      childJobIds
    }
  }
`;

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 py-1 text-sm">
      <dt className="w-40 shrink-0 text-gray-500">{label}</dt>
      <dd className="min-w-0 break-words text-gray-800">{children}</dd>
    </div>
  );
}

function when(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

// Where a job came from, in words. "cascade" alone does not tell somebody that
// nobody clicked anything.
const SOURCE_WORDS: Record<string, string> = {
  user: "a person asked for it",
  cascade: "queued automatically by another job",
  schedule: "a schedule came due",
  system: "the system started it",
};

export default function JobDetailPage({ jobId }: { jobId: number }) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const data = useLazyLoadQuery<jobDetailPage_Query>(
    jobDetailQuery,
    { jobId },
    { fetchKey: tick, fetchPolicy: tick > 0 ? "network-only" : "store-or-network" }
  );

  const job = data.accountJob as QueueJob | null;

  if (!job) {
    return (
      <div className="mx-auto max-w-3xl space-y-3 p-6">
        <h1 className="text-xl font-bold">Job #{jobId}</h1>
        <p className="text-sm text-gray-600">
          No job with that number on this account.
        </p>
        <Link to="/queue" className="text-sm text-blue-600 hover:underline">
          Back to the queue
        </Link>
      </div>
    );
  }

  const act = (fn: () => void) => {
    setBusy(true);
    fn();
  };

  const stop = (id: number) =>
    act(() =>
      commitMutation<queueFragment_CancelMutation>(environment, {
        mutation: cancelMutation,
        variables: { jobId: id },
        onCompleted: (response) => {
          setBusy(false);
          setNotice(
            response.accountJobCancel?.outcome === "stopping"
              ? "Asked it to stop — it will finish the step it is on."
              : "Stopped."
          );
          setTick((n) => n + 1);
        },
        onError: () => setBusy(false),
      })
    );

  const retry = (id: number) =>
    act(() =>
      commitMutation<queueFragment_EnqueueMutation>(environment, {
        mutation: enqueueMutation,
        variables: { kind: "", params: {}, retryJobId: id },
        onCompleted: () => {
          setBusy(false);
          setTick((n) => n + 1);
        },
        onError: () => setBusy(false),
      })
    );

  const params = (job.params ?? {}) as Record<string, unknown>;
  const children = job.childJobIds ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Link to="/queue" className="text-sm text-blue-600 hover:underline">
          ← Queue
        </Link>
        <h1 className="text-xl font-bold">
          {job.label} <span className="text-gray-400">#{job.jobId}</span>
        </h1>
      </div>

      {notice && (
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
          {notice}
        </div>
      )}

      <JobCard job={job} busy={busy} onStop={stop} onRetry={retry} />

      <div className="rounded border border-gray-200 bg-white p-4">
        <h2 className="mb-2 text-sm font-semibold text-gray-700">
          The record
        </h2>
        <dl>
          <Fact label="Kind">{job.kind}</Fact>
          <Fact label="Why it ran">
            {SOURCE_WORDS[job.source] ?? job.source}
            {job.requestedBy ? ` — ${job.requestedBy}` : ""}
          </Fact>
          <Fact label="Queued">{when(job.createdAt)}</Fact>
          <Fact label="Started">{when(job.startedAt)}</Fact>
          <Fact label="Finished">{when(job.finishedAt)}</Fact>
          <Fact label="Took">{elapsed(job) ?? "—"}</Fact>
          <Fact label="Usually takes">
            {job.typicalSeconds != null
              ? humanDuration(job.typicalSeconds)
              : "not enough history to say"}
          </Fact>
          <Fact label="Attempts">{job.attempts}</Fact>
          {job.parentJobId && (
            <Fact label="Queued by">
              <Link
                to="/queue/$jobId"
                params={{ jobId: String(job.parentJobId) }}
                className="text-blue-600 hover:underline"
              >
                job #{job.parentJobId}
              </Link>
            </Fact>
          )}
          {children.length > 0 && (
            <Fact label="It queued">
              {children.map((id, i) => (
                <span key={id}>
                  {i > 0 ? ", " : ""}
                  <Link
                    to="/queue/$jobId"
                    params={{ jobId: String(id) }}
                    className="text-blue-600 hover:underline"
                  >
                    #{id}
                  </Link>
                </span>
              ))}
            </Fact>
          )}
          {job.supersededByJobId && (
            <Fact label="Superseded by">
              <Link
                to="/queue/$jobId"
                params={{ jobId: String(job.supersededByJobId) }}
                className="text-blue-600 hover:underline"
              >
                job #{job.supersededByJobId}
              </Link>
            </Fact>
          )}
        </dl>
      </div>

      {Object.keys(params).length > 0 && (
        <div className="rounded border border-gray-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-700">
            What it was asked to do
          </h2>
          <dl>
            {Object.entries(params).map(([key, value]) => (
              <Fact key={key} label={key}>
                {typeof value === "object" && value !== null
                  ? JSON.stringify(value)
                  : String(value)}
              </Fact>
            ))}
          </dl>
        </div>
      )}

      {job.outcome.length > 0 && (
        <div className="rounded border border-gray-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-700">
            What it produced
          </h2>
          <dl>
            {job.outcome.map((entry) => (
              <Fact key={entry.key} label={entry.key}>
                {entry.value}
              </Fact>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
